import { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { Sidebar } from './sidebar';

interface LayoutProps {
  children?: ReactNode;
}

export function DashboardLayout({ children }: LayoutProps) {
  const { user, initialized } = useAuthStore(state => ({
    user: state.user,
    initialized: state.initialized
  }));
  const location = useLocation();

  // Show loading spinner while checking auth (only on initial load)
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-muted-foreground border-t-transparent"></div>
          <div className="flex flex-col items-center">
            <img src="/bevpass-logo.svg" alt="BevPass" className="h-8 w-auto" />
            <p className="text-sm text-muted-foreground text-center mt-2">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Redirect merchant users to their specific dashboard if they're at the root
  const isMerchantUser = user.role === 'merchant_owner' || user.role === 'merchant_viewer';
  if (isMerchantUser && location.pathname === '/dashboard') {
    if (user.applicationId) {
      // They're still in setup
      return <Navigate to="/merchants/onboarding" replace />;
    }

    if (user.merchantIds && user.merchantIds.length > 0) {
      // They have a merchant, go to merchant dashboard
      return <Navigate to="/dashboard" replace />;
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Content area takes full viewport height; only main scrolls */}
      <div className="flex h-screen overflow-hidden">
        <Sidebar />
        <main className="flex-1 p-6 overflow-y-auto">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
}

export function AuthLayout({ children }: LayoutProps) {
  const { user, initialized } = useAuthStore(state => ({
    user: state.user,
    initialized: state.initialized
  }));
  const location = useLocation();

  // Wait for auth to initialize
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-muted-foreground border-t-transparent"></div>
          <div className="flex flex-col items-center">
            <img src="/bevpass-logo.svg" alt="BevPass" className="h-8 w-auto" />
            <p className="text-sm text-muted-foreground text-center mt-2">Authenticating...</p>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to dashboard if already authenticated
  if (user) {
    const from = location.state?.from?.pathname || '/dashboard';
    return <Navigate to={from} replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      {children || <Outlet />}
    </div>
  );
}
